// Argus — NotificationsBell: IconButton cu badge unread + Popover history.

import { useState } from 'react';
import {
  IconButton,
  Badge,
  Tooltip,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type Notification,
  type NotificationSeverity,
} from '../../lib/notifications';
import { dayjs } from '../../lib/dayjsSetup';

const POLL_INTERVAL_MS = 30_000;

const SEVERITY_ICONS: Record<NotificationSeverity, JSX.Element> = {
  info: <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />,
  warning: <WarningAmberIcon fontSize="small" sx={{ color: 'warning.main' }} />,
  error: <ErrorOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />,
  success: <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />,
};

export function NotificationsBell(): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => fetchNotifications(false, 50),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch { /* swallow */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch { /* swallow */ }
  };

  const unreadCount = data?.unreadCount ?? 0;
  const items = data?.items ?? [];

  return (
    <>
      <Tooltip title={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}>
        <IconButton size="small" onClick={handleOpen} sx={{ mr: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2">Notifications</Typography>
          {unreadCount > 0 ? (
            <Button size="small" onClick={() => void handleMarkAllRead()}>
              Mark all read
            </Button>
          ) : null}
        </Box>

        {isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              No notifications.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 400, overflow: 'auto' }}>
            {items.map((n, idx) => (
              <NotificationRow
                key={n.id}
                notification={n}
                divider={idx < items.length - 1}
                onMarkRead={() => void handleMarkRead(n.id)}
              />
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}

interface NotificationRowProps {
  notification: Notification;
  divider: boolean;
  onMarkRead: () => void;
}

function NotificationRow({ notification, divider, onMarkRead }: NotificationRowProps): JSX.Element {
  const isUnread = notification.readAt === null;
  return (
    <>
      <ListItem
        onClick={isUnread ? onMarkRead : undefined}
        sx={{
          py: 1,
          backgroundColor: isUnread ? 'action.hover' : 'transparent',
          cursor: isUnread ? 'pointer' : 'default',
          '&:hover': isUnread ? { backgroundColor: 'action.selected' } : {},
        }}
      >
        <Box sx={{ mr: 1, mt: 0.5 }}>{SEVERITY_ICONS[notification.severity]}</Box>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ fontWeight: isUnread ? 600 : 400 }}>
              {notification.title || notification.message}
            </Typography>
          }
          secondary={
            <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {notification.title ? `${notification.message} · ` : ''}{dayjs(notification.createdAt).fromNow()}
            </Typography>
          }
        />
      </ListItem>
      {divider ? <Divider /> : null}
    </>
  );
}
